"use client";

import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { RefreshCw, Search, Eye, Play, ExternalLink } from "lucide-react";
import PermissionGuard from "@/components/PermissionGuard";
import { PermissionSubject } from "@/utils/permissions";
import { useKKMovieStore } from "@/stores/kkmovieStore";
import { useDebounce } from "@/hooks/useDebounce";
import { KKMovie, KKMovieStatusEnum, KKMovieTypeEnum } from "@/types/kkmovie";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { kkphimSyncService } from "@/services/kkphimSyncService";
import { ophimSyncService } from "@/services/ophimSyncService";
import { toast } from "sonner";

const statusLabels: Record<KKMovieStatusEnum, { label: string; class: string }> = {
  [KKMovieStatusEnum.COMPLETE]: { label: "Hoàn thành", class: "bg-green-100 text-green-800" },
  [KKMovieStatusEnum.ONGOING]: { label: "Đang chiếu", class: "bg-blue-100 text-blue-800" },
  [KKMovieStatusEnum.UPCOMING]: { label: "Sắp chiếu", class: "bg-yellow-100 text-yellow-800" },
};

const typeLabels: Record<KKMovieTypeEnum, string> = {
  [KKMovieTypeEnum.SERIES]: "Phim bộ",
  [KKMovieTypeEnum.SINGLE]: "Phim lẻ",
  [KKMovieTypeEnum.ANIME]: "Anime",
  [KKMovieTypeEnum.CINEMA]: "Chiếu rạp",
};

const sourceLabels: Record<string, { label: string; class: string }> = {
  KKphim: { label: "KKPhim", class: "bg-purple-100 text-purple-800" },
  OPhim: { label: "OPhim", class: "bg-pink-100 text-pink-800" },
  dual: { label: "Dual", class: "bg-emerald-100 text-emerald-800" },
};

export default function KKMoviePage() {
  const navigate = useNavigate();
  const {
    kkmovieList,
    pagination,
    filters,
    isLoading,
    fetchKKMovies,
    setFilters,
  } = useKKMovieStore();

  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 500);
  const isSearching = search !== debounced;
  const [syncingSlug, setSyncingSlug] = useState<string | null>(null);

  useEffect(() => {
    setFilters({ search: debounced });
  }, [debounced, setFilters]);

  useEffect(() => {
    fetchKKMovies();
  }, [filters, fetchKKMovies]);

  const handleRefresh = () => {
    fetchKKMovies();
  };

  const handlePage = (page: number) => {
    fetchKKMovies({ page, limit: pagination.limit });
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setFilters({ search });
  };

  const handleStatusFilter = (status: KKMovieStatusEnum | '') => {
    setFilters({ status });
  };

  const handleTypeFilter = (type: KKMovieTypeEnum | '') => {
    setFilters({ type });
  };

  const handleSourceFilter = (source: string) => {
    setFilters({ source });
  };

  const handleViewEpisodes = (movie: KKMovie) => {
    navigate(ROUTES.KKMOVIE_EPISODES.replace(':id', movie._id));
  };

  const handleSyncSingle = async (movie: KKMovie) => {
    setSyncingSlug(movie.url);
    try {
      if (movie.source === 'dual') {
        // Sync từ cả 2 nguồn
        const [kkRes, oRes] = await Promise.all([
          kkphimSyncService.syncSingleMovie(movie.url),
          ophimSyncService.syncSingleMovie(movie.url),
        ]);
        if (kkRes.ok || oRes.ok) {
          toast.success(`Đã sync thành công phim "${movie.name}" từ cả 2 nguồn`);
          fetchKKMovies();
        } else {
          toast.error("Không thể sync phim");
        }
      } else {
        const syncService = movie.source === 'OPhim' ? ophimSyncService : kkphimSyncService;
        const res = await syncService.syncSingleMovie(movie.url);
        if (res.ok) {
          toast.success(`Đã sync thành công phim "${movie.name}" từ ${movie.source || 'KKPhim'}`);
          fetchKKMovies();
        } else {
          toast.error("Không thể sync phim");
        }
      }
    } catch (error) {
      toast.error("Lỗi khi sync phim");
    } finally {
      setSyncingSlug(null);
    }
  };

  const getExternalUrl = (movie: KKMovie) => {
    if (movie.source === 'OPhim') {
      return `https://ophim1.com/phim/${movie.url}`;
    }
    return `https://phimapi.com/phim/${movie.url}`;
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý KKMovie
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500">Danh sách phim từ KKPhim & OPhim</p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {pagination.total} phim
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleRefresh} variant="outline" className="flex items-center gap-2 hover:cursor-pointer">
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              Làm mới
            </Button>
            <PermissionGuard subject={PermissionSubject.KKPHIM_SYNC} action="read">
              <Button
                onClick={() => navigate(ROUTES.KKPHIM_SYNC)}
                className="bg-green-600 text-white hover:bg-green-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
              >
                <RefreshCw size={20} />
                KKPhim Sync
              </Button>
            </PermissionGuard>
            <PermissionGuard subject={PermissionSubject.OPHIM_SYNC} action="read">
              <Button
                onClick={() => navigate(ROUTES.OPHIM_SYNC)}
                className="bg-pink-600 text-white hover:bg-pink-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
              >
                <RefreshCw size={20} />
                OPhim Sync
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-7 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm theo tên phim..."
                className="h-10 w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => handleStatusFilter(e.target.value as KKMovieStatusEnum | '')}
              className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả trạng thái</option>
              <option value={KKMovieStatusEnum.ONGOING}>Đang chiếu</option>
              <option value={KKMovieStatusEnum.COMPLETE}>Hoàn thành</option>
              <option value={KKMovieStatusEnum.UPCOMING}>Sắp chiếu</option>
            </select>
            <select
              value={filters.type}
              onChange={(e) => handleTypeFilter(e.target.value as KKMovieTypeEnum | '')}
              className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả loại</option>
              <option value={KKMovieTypeEnum.SERIES}>Phim bộ</option>
              <option value={KKMovieTypeEnum.SINGLE}>Phim lẻ</option>
              <option value={KKMovieTypeEnum.ANIME}>Anime</option>
              <option value="cinema">Chiếu rạp</option>
            </select>
            <select
              value={filters.source}
              onChange={(e) => handleSourceFilter(e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả nguồn</option>
              <option value="KKphim">KKPhim</option>
              <option value="OPhim">OPhim</option>
              <option value="dual">Dual</option>
            </select>
            <Button type="submit" disabled={isLoading || isSearching} className="h-10 hover:cursor-pointer">
              {isSearching ? "Đang tìm..." : "Tìm kiếm"}
            </Button>
          </form>
        </div>

        {/* Table */}
        <div className="shadow-lg rounded-xl bg-white border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tên phim</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nguồn</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trạng thái</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Loại</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tập</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Năm</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {kkmovieList.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <Play className="h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-gray-600 font-medium">Chưa có phim nào</p>
                            <p className="text-gray-400 text-sm mt-1">Phim sẽ được thêm khi sync từ KKPhim hoặc OPhim</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      kkmovieList.map((movie) => (
                        <tr key={movie._id} className="transition-all hover:bg-gray-50/60">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {movie.posters?.[0] && (
                                <img
                                  src={movie.posters[0]}
                                  alt={movie.name}
                                  className="w-12 h-16 object-cover rounded"
                                />
                              )}
                              <div>
                                <div className="font-medium text-gray-900">{movie.name}</div>
                                {movie.origin_name && (
                                  <div className="text-sm text-gray-500">{movie.origin_name}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${sourceLabels[movie.source]?.class || 'bg-gray-100 text-gray-800'}`}>
                              {sourceLabels[movie.source]?.label || movie.source || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusLabels[movie.status]?.class || 'bg-gray-100 text-gray-800'}`}>
                              {statusLabels[movie.status]?.label || movie.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {typeLabels[movie.type] || movie.type}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {movie.current_episode || 0}/{movie.total_episode || '?'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {movie.year || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="hover:cursor-pointer"
                                onClick={() => handleViewEpisodes(movie)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Tập
                              </Button>
                              <PermissionGuard subject={movie.source === 'OPhim' ? PermissionSubject.OPHIM_SYNC : PermissionSubject.KKPHIM_SYNC} action="update">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="hover:cursor-pointer"
                                  disabled={syncingSlug === movie.url}
                                  onClick={() => handleSyncSingle(movie)}
                                >
                                  <RefreshCw className={`h-4 w-4 mr-1 ${syncingSlug === movie.url ? 'animate-spin' : ''}`} />
                                  Sync
                                </Button>
                              </PermissionGuard>
                              <a
                                href={getExternalUrl(movie)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-8 px-3 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
                    <div>
                      <p className="text-sm text-gray-700">
                        Trang <span className="font-medium">{pagination.page}</span> / {pagination.totalPages}
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button onClick={() => handlePage(Math.max(1, pagination.page - 1))} disabled={pagination.page === 1} variant="outline" size="sm" className="rounded-l-md hover:cursor-pointer">
                          Trước
                        </Button>
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.page >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.page - 2 + i;
                          }
                          return (
                            <Button key={pageNum} onClick={() => handlePage(pageNum)} variant={pageNum === pagination.page ? "default" : "outline"} size="sm" className="rounded-none hover:cursor-pointer">
                              {pageNum}
                            </Button>
                          );
                        })}
                        <Button onClick={() => handlePage(Math.min(pagination.totalPages, pagination.page + 1))} disabled={pagination.page === pagination.totalPages} variant="outline" size="sm" className="rounded-r-md hover:cursor-pointer">
                          Sau
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
